import is from '@sindresorhus/is';
import clsx from 'clsx';
import type { FunctionComponent, HTMLAttributes, Ref } from 'react';
import { createElement } from 'react';

export const TimelineContainer: FunctionComponent<HTMLAttributes<HTMLDivElement>> = ({ className, ...props }) =>
    createElement('div', { className: clsx('self-stretch flex flex-col font-sans', className), ...props });

export const GroupContainer: FunctionComponent<HTMLAttributes<HTMLDivElement>> = ({ className, ...props }) =>
    createElement('div', { className: clsx('flex flex-row gap-xl relative', className), ...props });

export const TimelineVisual: FunctionComponent<HTMLAttributes<HTMLDivElement>> = ({ className, ...props }) =>
    createElement('div', {
        className: clsx('flex items-center flex-col relative w-[0.75rem]', className),
        ...props,
    });

export const TimelineDot: FunctionComponent<HTMLAttributes<HTMLDivElement>> = ({ className, ...props }) =>
    createElement('div', {
        className: clsx('bg-black-700 rounded-full shrink-0 h-[0.5625rem] mt-[0.3125rem] w-[0.5625rem]', className),
        ...props,
    });

const verticalDottedGradient =
    'repeating-linear-gradient(transparent 0px, transparent 2px, var(--color-gray-500) 2px, var(--color-gray-500) 5px)';

export const TimelineLine: FunctionComponent<HTMLAttributes<HTMLDivElement> & { readonly isLastGroup: boolean }> = ({
    className,
    isLastGroup,
    ...props
}) =>
    createElement('div', {
        className: clsx('left-1/2 absolute -translate-x-1/2 w-[0.0625rem]', className),
        style: {
            background: verticalDottedGradient,
            bottom: isLastGroup ? 'var(--spacing-xl)' : 0,
            top: '1.5rem',
            ...props.style,
        },
        ...props,
    });

export const GroupContent: FunctionComponent<HTMLAttributes<HTMLDivElement>> = ({ className, ...props }) =>
    createElement('div', { className: clsx('flex flex-1 flex-col gap-xl', className), ...props });

export const GroupLabel: FunctionComponent<HTMLAttributes<HTMLDivElement>> = ({ className, ...props }) =>
    createElement('div', {
        className: clsx('text-sm font-normal leading-[1.25rem] text-black-900', className),
        ...props,
    });

export const ItemsContainer: FunctionComponent<
    HTMLAttributes<HTMLDivElement> & { readonly ref?: Ref<HTMLDivElement> }
> = ({ className, ...props }) =>
    createElement('div', { className: clsx('flex flex-col gap-xl pb-xl', className), ...props });

export const ItemContainer: FunctionComponent<HTMLAttributes<HTMLDivElement> & { readonly disabled?: boolean }> = ({
    className,
    disabled = false,
    onClick,
    ...props
}) =>
    createElement('div', {
        className: clsx(
            'flex items-center rounded-sm gap-m justify-between -ml-s p-s transition-[background-color] duration-200 ease-in-out',
            disabled
                ? 'cursor-default opacity-50 pointer-events-none'
                : is.nullOrUndefined(onClick)
                  ? 'cursor-default'
                  : 'cursor-pointer hover:bg-gray-200',
            className,
        ),
        onClick,
        ...props,
    });

export const ItemContent: FunctionComponent<HTMLAttributes<HTMLDivElement>> = ({ className, ...props }) =>
    createElement('div', { className: clsx('flex items-start flex-1 gap-m justify-start', className), ...props });

export { IconIndicator as IconWrapper } from '../icon-indicator/IconIndicator';

export const ItemDetails: FunctionComponent<HTMLAttributes<HTMLDivElement>> = ({ className, ...props }) =>
    createElement('div', { className: clsx('flex flex-col justify-start', className), ...props });

export const ItemTitle: FunctionComponent<HTMLAttributes<HTMLSpanElement>> = ({ className, ...props }) =>
    createElement('span', {
        className: clsx('text-sm  leading-[1.25rem] text-black-700', className),
        ...props,
    });

export const ItemSubtitle: FunctionComponent<HTMLAttributes<HTMLParagraphElement>> = ({ className, ...props }) =>
    createElement('p', {
        className: clsx('text-xxs font-normal leading-[1rem] text-black-600 m-0 mt-xs', className),
        ...props,
    });

export const ItemAction: FunctionComponent<HTMLAttributes<HTMLDivElement>> = ({ className, ...props }) =>
    createElement('div', { className: clsx('flex items-center shrink-0', className), ...props });

export const FooterContainer: FunctionComponent<HTMLAttributes<HTMLDivElement>> = ({ className, ...props }) =>
    createElement('div', { className: clsx('flex flex-row gap-xl', className), ...props });

export const FooterText: FunctionComponent<HTMLAttributes<HTMLSpanElement>> = ({ className, ...props }) =>
    createElement('span', {
        className: clsx('text-xxs font-normal leading-[1rem] text-gray-600', className),
        ...props,
    });
